/// <reference types="chrome"/>

import {
  AccountInfo,
  AccountStatus,
  Alarm,
  AlarmOptions,
  DeviceDetails,
  IdleState,
  Manifest,
  NetworkDetails,
  ProfileDetails,
  UserInfo,
} from '../extension-api-interface/extension-api.interfaces';
import { IExtensionApiStrategy } from '../extension-api-interface/extension-api.strategy-interface';

export class ChromeExtensionApiStrategy extends IExtensionApiStrategy {
  public override setIconBadge = async ({
    backgroundColor,
    text,
    textColor,
  }: {
    backgroundColor: string;
    text: string;
    textColor: string;
  }): Promise<void> => {
    await chrome.action.setBadgeBackgroundColor({ color: backgroundColor });
    await chrome.action.setBadgeTextColor({ color: textColor });
    await chrome.action.setBadgeText({ text });
  };

  public override getExtensionId = (): string => chrome.runtime.id;

  public override reloadExtension = async (): Promise<void> => chrome.runtime.reload();

  public override clearStorage = (): Promise<void> => chrome.browsingData.removeCache({});

  public override sendMessage = async <ResponseType = any>(message: string): Promise<ResponseType | undefined> =>
    chrome.runtime.sendMessage<string, ResponseType | undefined>(message);

  public override addMessageListener = (messageToWatch: string, handlerFunction: <T>() => Promise<T>) => {
    chrome.runtime.onMessage.addListener((message, sender, sendResponse) => {
      if (message === messageToWatch) {
        handlerFunction()
          .then((response) => sendResponse(response))
          .catch(() => sendResponse(undefined));
      }
      return true; // Required to keep the channel open for sendResponse
    });
  };

  public override showNotification = (
    notificationId: string,
    options: chrome.notifications.NotificationOptions<true>
  ): void => chrome.notifications.create(notificationId, options);

  public override clearNotification = (notificationId: string): void => chrome.notifications.clear(notificationId);

  public override setNotificationListener = (
    listener: (notificationId: string, buttonIndex: number) => Promise<void>
  ): void => chrome.notifications.onButtonClicked.addListener(listener);

  public override createTab = async (urlOrOptions: string | Record<string, string>): Promise<chrome.tabs.Tab> => {
    if (typeof urlOrOptions === 'string') return chrome.tabs.create({ url: urlOrOptions });
    return chrome.tabs.create(urlOrOptions as chrome.tabs.CreateProperties);
  };

  public getAlarm = async (triggerId: string): Promise<Alarm> => chrome.alarms.get(triggerId);

  public createAlarm = async (triggerId: string, options: AlarmOptions): Promise<void> =>
    chrome.alarms.create(triggerId, options);

  public clearAlarm = async (triggerId: string): Promise<boolean> => chrome.alarms.clear(triggerId);

  public clearAllAlarms = async (): Promise<boolean> => chrome.alarms.clearAll();

  public getAllAlarms = async (): Promise<Alarm[]> => chrome.alarms.getAll();

  public addAlarmListener = (callback: (alarm: Alarm) => void): void => {
    chrome.alarms.onAlarm.addListener(callback);
  };

  public onInstalled = (callback: () => void): void => {
    chrome.runtime.onInstalled.addListener(callback);
  };

  public onStartup = (callback: () => void): void => {
    chrome.runtime.onStartup.addListener(callback);
  };

  // Note: queryState should return a promise, but the types dont reflect it yet. So we wrap it in a promise.
  public getIdleState = async (detectionIntervalInSeconds: number): Promise<IdleState> =>
    new Promise((resolve) => {
      chrome.idle.queryState(detectionIntervalInSeconds, (state: IdleState) => resolve(state));
    });

  public setIdleStateDetectionInterval = (intervalInSeconds: number): void =>
    chrome.idle.setDetectionInterval(intervalInSeconds);

  public addIdleStateChangeListener = (listener: (newState: IdleState) => void): void => {
    chrome.idle.onStateChanged.addListener(listener);
  };

  public setLocalStorage = async (items: { [key: string]: any }): Promise<void> => chrome.storage.local.set(items);

  public getLocalStorage = async <T = any>(keys: string | string[] | { [key: string]: any } | null): Promise<T> =>
    chrome.storage.local.get(keys) as Promise<T>;

  public removeLocalStorage = async (keys: string | string[]): Promise<void> => chrome.storage.local.remove(keys);

  public clearLocalStorage = async (): Promise<void> => chrome.storage.local.clear();

  public getUserProfile = async (details: ProfileDetails = { accountStatus: AccountStatus.SYNC }): Promise<UserInfo> =>
    chrome.identity.getProfileUserInfo(details);

  public setUserSignInListener = (listener: (account: AccountInfo, signedIn: boolean) => void): void => {
    chrome.identity.onSignInChanged.addListener(listener);
  };

  public getManifest = (): Manifest => chrome.runtime.getManifest();

  public getDevice = async (): Promise<DeviceDetails> => {
    const deviceIdPromise: Promise<string> = new Promise((resolve) => {
      chrome.enterprise.deviceAttributes.getDirectoryDeviceId((deviceId: string) => resolve(deviceId));
    });

    const serialNumberPromise: Promise<string> = new Promise((resolve, reject) => {
      if (!('enterprise' in chrome)) {
        reject(new Error('Device Not Managed'));
      }
      chrome.enterprise.deviceAttributes.getDeviceSerialNumber((serialNumber: string) => resolve(serialNumber));
    });

    const assetIdPromise: Promise<string> = new Promise((resolve, reject) => {
      if (!('enterprise' in chrome)) {
        reject(new Error('Device Not Managed'));
      }
      chrome.enterprise.deviceAttributes.getDeviceAssetId((assetId: string) => resolve(assetId));
    });

    const deviceDetails = {
      id: await deviceIdPromise,
      serialNumber: await serialNumberPromise,
      assetId: await assetIdPromise,
    };

    if (deviceDetails.id === null || deviceDetails.serialNumber === null) {
      throw new Error('Device Not Managed');
    }

    return deviceDetails;
  };

  public getNetworkDetails = async (): Promise<NetworkDetails> =>
    new Promise((resolve, reject) => {
      chrome.enterprise.networkingAttributes.getNetworkDetails((networkDetails) => {
        if (chrome.runtime.lastError) {
          reject(new Error('Device Not Managed'));
        }
        resolve(networkDetails);
      });
    });
}
